import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Gift, Copy, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GiftVoucherSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [voucherCode, setVoucherCode] = useState(null);

  useEffect(() => {
    const checkPayment = async () => {
      const sessionId = searchParams.get("session_id");
      if (!sessionId) {
        setStatus("error");
        return;
      }

      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000;

      const poll = async () => {
        try {
          const response = await axios.get(`${API}/checkout/status/${sessionId}`);
          
          if (response.data.payment_status === "paid") {
            setStatus("success");
            // The voucher code would be in the email, but we can show a success message
            setVoucherCode("GIFT-" + sessionId.slice(0, 8).toUpperCase());
          } else if (response.data.status === "expired") {
            setStatus("expired");
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, pollInterval);
          } else {
            setStatus("pending");
          }
        } catch (error) {
          console.error("Error checking payment:", error);
          if (attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, pollInterval);
          } else {
            setStatus("error");
          }
        }
      };

      poll();
    };

    checkPayment();
  }, [searchParams]);

  const copyCode = () => {
    if (voucherCode) {
      navigator.clipboard.writeText(voucherCode);
      toast.success("Voucher code copied to clipboard!");
    }
  };

  const getStatusMessage = () => {
    if (status === "expired") return { title: "Session Expired", message: "Your payment session has expired. Please try again.", button: "Try Again" };
    if (status === "pending") return { title: "Payment Processing", message: "Your payment is being processed. You'll receive the voucher via email once complete.", button: "Return to Vouchers" };
    return { title: "Something Went Wrong", message: "We couldn't verify your payment. Please try again or contact us.", button: "Try Again" };
  };

  return (
    <div className="min-h-screen pt-32 pb-24 flex items-center" data-testid="gift-voucher-success-page">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-lg mx-auto text-center"
        >
          {status === "checking" && (
            <>
              <div className="w-16 h-16 mx-auto mb-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <h1 className="heading-lg mb-4">Processing Payment...</h1>
              <p className="body-text text-muted-foreground">
                Please wait while we confirm your purchase.
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 mx-auto mb-8 bg-primary/10 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-primary" />
              </div>
              <h1 className="heading-lg mb-4 text-foreground">
                Gift Voucher <em className="italic">Purchased!</em>
              </h1>
              <p className="body-text text-muted-foreground mb-8">
                Thank you for your purchase. The gift voucher details have been sent 
                to your email address.
              </p>

              {voucherCode && (
                <div className="bg-muted p-8 border border-border mb-8">
                  <Gift className="w-8 h-8 text-primary mx-auto mb-4" />
                  <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">
                    Voucher Code
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <span className="font-serif text-2xl text-foreground tracking-wider">
                      {voucherCode}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={copyCode}
                      className="text-primary hover:text-primary/80"
                      data-testid="copy-code-btn"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-4">
                    Valid for 12 months from purchase date
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/">
                  <Button
                    variant="outline"
                    className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                    data-testid="return-home-btn"
                  >
                    Return Home
                  </Button>
                </Link>
                <Link to="/gift-vouchers">
                  <Button
                    className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
                    data-testid="buy-another-btn"
                  >
                    Buy Another Voucher
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </>
          )}

          {(status === "error" || status === "expired" || status === "pending") && (
            <>
              <div className="w-20 h-20 mx-auto mb-8 bg-secondary/20 rounded-full flex items-center justify-center">
                <Gift className="w-10 h-10 text-secondary" />
              </div>
              <h1 className="heading-lg mb-4 text-foreground">
                {getStatusMessage().title}
              </h1>
              <p className="body-text text-muted-foreground mb-8">
                {getStatusMessage().message}
              </p>
              <Link to="/gift-vouchers">
                <Button
                  className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
                  data-testid="try-again-btn"
                >
                  {getStatusMessage().button}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
